/** Copyright 2023 Midas Healthcare Solutions - All Rights Reserved **/
import { Injectable } from '@angular/core';
import { ApiService, EOverViewPercentFullLimits, OverviewView } from '@midas/shared/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@nrwl/angular';
import { map } from 'rxjs';
import * as OverviewViewActions from './overview-views.actions';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ViewsFilter } from 'libs/overview/shell/src/lib/overview-views/views-filter/views-filter';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { ViewsLocations } from 'libs/overview/shell/src/lib/overview-views/views-filter/locations.interface';
import { statuses } from '../../../../shell/src/lib/overview-views/cart-status.enum';

@Injectable()
export class OverviewViewsEffects {
  readonly queryOverviewViews$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewViewActions.queryOverviewViews),
      fetch({
        run: () => {
          return this.api.get<{ carts: OverviewView[] }>('/overview/views/').pipe(
            map((res) => {
              return OverviewViewActions.queryOverviewViewsSuccess({
                carts: res.carts,
                locations: this.filterLocationFromViews(res.carts),
              });
            }),
          );
        },
        onError: (action, error) => {
          return OverviewViewActions.queryOverviewViewsError({ error });
        },
      }),
    );
  });

  readonly queryOverviewViewBySearchValue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewViewActions.queryOverviewViewBySearchValue),
      fetch({
        run: (searchValue) => {
          return this.api.get<{ carts: OverviewView[] }>('/overview/views/').pipe(
            map((res) => {
              const filteredData = this.filterViews(searchValue, res.carts);
              return OverviewViewActions.queryOverviewViewBySearchValueSuccess({ carts: filteredData });
            }),
          );
        },
        onError: (action, error) => {
          return OverviewViewActions.queryOverviewViewBySearchValueError({ error });
        },
      }),
    );
  });

  readonly queryOverviewViewGetLocations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OverviewViewActions.queryOverviewViewGetLocations),
      fetch({
        run: () => {
          return this.api.get<{ locations: ViewsLocations[] }>('/overview/views/locations/').pipe(
            map((res) => {
              return OverviewViewActions.queryOverviewViewGetLocationsSuccess({ locations: res.locations });
            }),
          );
        },
        onError: (action, error) => {
          return OverviewViewActions.queryOverviewViewGetLocationsError({ error });
        },
      }),
    );
  });

  filterViews = (filter: ViewsFilter, data: OverviewView[]) => {
    const { locationIds, statusIds, searchValue } = filter;

    if (locationIds.length) {
      data = data.filter((view) => {
        return locationIds.length > 0 && locationIds.indexOf(view.locationId) >= 0;
      });
    }

    if (searchValue !== '') {
      data = this.filterBySearchValue(searchValue, data);
    }

    if (statusIds.length) {
      data = data.filter((view) => {
        return statusIds.length > 0 && statusIds.indexOf(this.filterStatusId(view)) >= 0;
      });
    }

    return data;
  };

  filterBySearchValue = (searchValue: string, data: OverviewView[]): OverviewView[] => {
    return data.filter((view) => {
      return (
        view.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        view.locationName.toLowerCase().includes(searchValue.toLowerCase()) ||
        view.organizationName.toLowerCase().includes(searchValue.toLowerCase())
      );
    });
  };

  filterLocationFromViews = (data: OverviewView[]): ViewsLocations[] => {
    const groupOfLocations: ViewsLocations[] = [];

    data.map((view) => {
      const index = groupOfLocations.findIndex((group) => group.id === view.locationId);
      index === -1 && groupOfLocations.push({ id: view.locationId, name: view.locationName });
    });

    return groupOfLocations;
  };

  filterStatusId = (item: OverviewView): number => {
    return item.inUse
      ? statuses[0].id
      : item.collectionBins.find((bin) => bin.percentFull === EOverViewPercentFullLimits.full)
      ? statuses[1].id
      : item.collectionBins.find((bin) => bin.percentFull >= EOverViewPercentFullLimits.almostFull)
      ? statuses[2].id
      : statuses[3].id;
  };
  constructor(private actions$: Actions, private api: ApiService) {}
}
